import { Link, createFileRoute } from '@tanstack/react-router';

export const Route = createFileRoute('/opt-in-documentation')({
  component: OptInDocumentationPage,
});

const CONTACT_EMAIL = 'support@pirouettehq.com';

function OptInDocumentationPage() {
  return (
    <main
      id="main"
      className="min-h-dvh bg-[var(--color-surface-sunken)] px-6 py-10 text-[var(--color-ink)] sm:px-10 lg:px-16"
      data-testid="opt-in-documentation-page"
    >
      <div className="mx-auto flex max-w-5xl flex-col gap-10">
        <nav aria-label="Public navigation" className="flex items-center justify-between gap-6">
          <Link
            to="/"
            className="font-[var(--font-display)] tracking-[var(--tracking-wide)] text-[var(--text-lg)] uppercase"
          >
            Pirouette
          </Link>
          <div className="flex items-center gap-5 text-[var(--text-sm)]">
            <Link to="/sms-consent" className="text-[var(--color-ink-muted)] hover:underline">
              SMS consent terms
            </Link>
            <Link to="/privacy" className="text-[var(--color-ink-muted)] hover:underline">
              Privacy
            </Link>
            <Link to="/terms" className="text-[var(--color-ink-muted)] hover:underline">
              Terms
            </Link>
            <Link to="/contact" className="text-[var(--color-ink-muted)] hover:underline">
              Contact
            </Link>
          </div>
        </nav>

        <section className="max-w-3xl">
          <p className="font-[var(--font-display)] tracking-[var(--tracking-wide)] text-[var(--color-accent)] text-[var(--text-base)] uppercase">
            Opt-in documentation
          </p>
          <h1 className="mt-4 leading-[var(--leading-tight)] font-[var(--font-display)] tracking-[var(--tracking-tight)] text-[var(--text-display)]">
            Pirouette SMS opt-in workflow
          </h1>
          <p className="mt-6 leading-[var(--leading-relaxed)] text-[var(--color-ink-muted)] text-[var(--text-lg)]">
            This page documents the public opt-in flow for Pirouette transactional SMS alerts. SMS
            opt-in is optional and is not required to create an account, enroll in a class, purchase
            services, or use Pirouette.
          </p>
        </section>

        <section className="grid gap-5 lg:grid-cols-[0.9fr_1.1fr]">
          <article className="rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-surface)] p-6">
            <h2 className="font-semibold text-[var(--text-xl)]">Submission summary</h2>
            <dl className="mt-5 space-y-4 text-[var(--text-sm)]">
              <div>
                <dt className="font-medium text-[var(--color-ink)]">Business / program name</dt>
                <dd className="mt-1 text-[var(--color-ink-muted)]">Pirouette SMS alerts</dd>
              </div>
              <div>
                <dt className="font-medium text-[var(--color-ink)]">Opt-in type</dt>
                <dd className="mt-1 text-[var(--color-ink-muted)]">
                  Enrollment instructions available through email support
                </dd>
              </div>
              <div>
                <dt className="font-medium text-[var(--color-ink)]">Enrollment status</dt>
                <dd className="mt-1 text-[var(--color-ink-muted)]">
                  Phone number no longer publicly listed
                </dd>
              </div>
              <div>
                <dt className="font-medium text-[var(--color-ink)]">Message types</dt>
                <dd className="mt-1 text-[var(--color-ink-muted)]">
                  Transactional class schedule changes and payment issue alerts
                </dd>
              </div>
              <div>
                <dt className="font-medium text-[var(--color-ink)]">Support contact</dt>
                <dd className="mt-1">
                  <a href={`mailto:${CONTACT_EMAIL}`} className="text-[var(--color-accent)]">
                    {CONTACT_EMAIL}
                  </a>
                </dd>
              </div>
            </dl>
          </article>

          <article className="rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-surface)] p-6">
            <h2 className="font-semibold text-[var(--text-xl)]">What the user sees</h2>
            <div className="mt-5 rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-surface-sunken)] p-5">
              <p className="font-medium text-[var(--color-ink)]">Pirouette SMS enrollment</p>
              <p className="mt-3 leading-[var(--leading-relaxed)] text-[var(--color-ink-muted)]">
                Pirouette no longer publishes a phone number for new SMS enrollment. For current
                enrollment instructions or messaging assistance, email{' '}
                <a href={`mailto:${CONTACT_EMAIL}`} className="text-[var(--color-accent)]">
                  {CONTACT_EMAIL}
                </a>
                .
              </p>
              <p className="mt-3 leading-[var(--leading-relaxed)] text-[var(--color-ink-muted)]">
                SMS consent is optional and separate from Pirouette account access, enrollment,
                purchases, and general service terms. See the{' '}
                <Link to="/privacy" className="text-[var(--color-accent)]">
                  Privacy Policy
                </Link>{' '}
                and{' '}
                <Link to="/terms" className="text-[var(--color-accent)]">
                  Terms
                </Link>
                .
              </p>
            </div>
          </article>
        </section>

        <section className="rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-surface)] p-6">
          <h2 className="font-semibold text-[var(--text-xl)]">Compliance details</h2>
          <div className="mt-4 grid gap-4 leading-[var(--leading-relaxed)] text-[var(--color-ink-muted)] md:grid-cols-2">
            <p>
              Users can opt out at any time by replying <strong>STOP</strong>. Users can request
              assistance by replying <strong>HELP</strong> or emailing{' '}
              <a href={`mailto:${CONTACT_EMAIL}`} className="text-[var(--color-accent)]">
                {CONTACT_EMAIL}
              </a>
              .
            </p>
            <p>
              Mobile opt-in data and consent are not sold, rented, or shared with third parties for
              their marketing or promotional purposes. Review the{' '}
              <Link to="/privacy" className="text-[var(--color-accent)]">
                Privacy Policy
              </Link>{' '}
              and{' '}
              <Link to="/terms" className="text-[var(--color-accent)]">
                SMS Terms
              </Link>
              . Carriers are not liable for delayed or undelivered messages.
            </p>
          </div>
        </section>
      </div>
    </main>
  );
}
